// /brokers/integrations — broker provider-integration status (SM-716,
// SM-621 phase 08a) and, since SM-903, the launch URL each configured
// broker's provider registers.
//
// One row per broker node: whether an external credit provider is
// configured for it (CONFIGURED once it has a provider configuration,
// which does not mean enabled), the process-wide provider mode and
// third-party provider flag broker-service started with, and how many open
// third-party credit accounts the node services. Read-only; BROKER_VIEW.
//
// With BROKER_PROVIDER_VIEW as well, a Launch URL column shows, for the
// page's CONFIGURED rows, https://<host>/launch/<slug> per launch host
// with a copy button, the slug, Locked, environment and whether a launch
// would work — all from broker-service (GET /admin/brokers/provider-launch;
// the page never guesses readiness). The URL is text, never a link, and
// the copied value has no query: the provider appends its own one-time
// ?code= on every launch, so "?code={LAUNCH_CODE}" is muted example text.
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { BrokerStatusBadges } from "@/components/brokers/BrokerBadges";
import { integrationStatusLabel, integrationStatusTone } from "@/components/brokers/governance";
import {
  exampleLaunchOpenURL,
  launchNotReadyLabel,
  launchRegistrationURL,
  providerEnvironmentLabel,
} from "@/components/brokers/provider";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CopyButton } from "@/components/ui/CopyButton";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Pagination } from "@/components/ui/Pagination";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import { brokerKeys, fetchBrokerIntegrations, fetchBrokerProviderLaunch } from "@/lib/broker-queries";
import type { ProviderLaunchItem } from "@/lib/broker-types";
import { Perm } from "@/lib/perms";

const PAGE_SIZE = 25;

interface Search {
  page?: number;
}

export const Route = createFileRoute("/_app/brokers/integrations")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    page: Number.isFinite(Number(s.page)) && Number(s.page) > 0 ? Number(s.page) : undefined,
  }),
  component: BrokerIntegrationsPage,
});

const th = "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";
const note = "rounded-md border border-border bg-surface-2 px-4 py-3 text-xs text-t-2";

function BrokerIntegrationsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const canView = perms.has(Perm.BrokerView);
  const canSeeLaunch = perms.has(Perm.BrokerProviderView);
  const page = search.page ?? 0;
  const filter = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };

  const q = useQuery({
    queryKey: brokerKeys.integrations(filter),
    queryFn: () => fetchBrokerIntegrations(filter),
    placeholderData: keepPreviousData,
    enabled: canView,
  });
  const rows = q.data?.items ?? [];
  const anyConfigured = rows.some((r) => r.integration_status !== "NOT_CONFIGURED");
  const configuredIds = rows.filter((r) => r.integration_status === "CONFIGURED").map((r) => r.broker_id);

  // SM-903: only the page's CONFIGURED ids, and only with the provider
  // code — broker-service answers no ids with a 400, never "all brokers".
  const launchQ = useQuery({
    queryKey: brokerKeys.providerLaunch(configuredIds),
    queryFn: () => fetchBrokerProviderLaunch(configuredIds),
    enabled: canView && canSeeLaunch && configuredIds.length > 0,
  });
  const launch = launchQ.data;
  const launchByBroker = new Map((launch?.items ?? []).map((it) => [it.broker_id, it]));
  const showLaunchNotes = canSeeLaunch && configuredIds.length > 0;

  if (perms.loaded && !canView) {
    return (
      <EmptyState title="No access" description="You need the BROKER_VIEW permission to see broker integrations." />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Provider integrations</h1>
        <p className="text-sm text-muted">
          Whether each broker has an external credit provider configured, and the launch URL its provider registers.
        </p>
      </div>

      {q.data && rows.length > 0 && !anyConfigured ? (
        <p className={note}>No broker on this page has a provider configured.</p>
      ) : null}

      {showLaunchNotes ? (
        <div className="space-y-2">
          <p className={note}>
            The launch URL is what the provider registers. On every launch the provider appends its own one-time launch
            code. Never share a launch URL that contains a real code.
          </p>
          {launch && launch.launch_base_urls.length === 0 ? (
            <p className={note}>No launch host is configured for this console; only the path is shown.</p>
          ) : null}
          {launch && !launch.launch_active ? <p className={note}>Provider launch is off in this environment.</p> : null}
          {launchQ.isError ? (
            <p className={note}>Launch URLs could not be loaded. Reload the page to try again.</p>
          ) : null}
        </div>
      ) : null}

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Brokers</CardTitle>
          <span className="text-2xs text-muted">{q.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {q.isLoading || !perms.loaded ? (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : null}
          {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}
          {q.data && rows.length === 0 ? (
            <p className="px-5 py-16 text-center text-sm text-muted">No broker has been created yet.</p>
          ) : null}
          {rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Broker</th>
                    <th className={th}>Status</th>
                    <th className={th}>Integration</th>
                    {canSeeLaunch ? <th className={th}>Launch URL</th> : null}
                    <th className={th}>Provider mode</th>
                    <th className={th}>Third-party provider flag</th>
                    <th className={`${th} text-right`}>Third-party credit accounts</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.broker_id} className="border-b border-border/60 last:border-0">
                      <td className={`${td} px-5`}>
                        <Link to="/brokers/$id" params={{ id: r.broker_id }} className="font-medium text-t-1 hover:underline">
                          {r.display_name || r.broker_id}
                        </Link>
                        <div className="font-mono text-2xs text-muted">{r.broker_id}</div>
                        {r.broker_id !== r.root_broker_id ? (
                          <div className="font-mono text-2xs text-muted">{r.root_broker_id}</div>
                        ) : null}
                      </td>
                      <td className={td}>
                        <BrokerStatusBadges broker={{ status: r.status }} />
                      </td>
                      <td className={td}>
                        <Badge tone={integrationStatusTone(r.integration_status)}>
                          {integrationStatusLabel(r.integration_status)}
                        </Badge>
                      </td>
                      {canSeeLaunch ? (
                        <td className={td}>
                          <LaunchCell
                            item={launchByBroker.get(r.broker_id)}
                            bases={launch?.launch_base_urls ?? []}
                            loading={launchQ.isLoading && r.integration_status === "CONFIGURED"}
                          />
                        </td>
                      ) : null}
                      <td className={`${td} font-mono text-2xs`}>{r.provider_mode || "—"}</td>
                      <td className={td}>{r.third_party_provider ? <>On</> : <>Off</>}</td>
                      <td className={`${td} text-right font-mono`}>{r.external_credit_accounts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {q.data && q.data.page.total > 0 ? (
            <Pagination
              page={q.data.page}
              currentPage={page}
              onPageChange={(p) => void navigate({ search: { page: p > 0 ? p : undefined } })}
              itemLabel="entries"
            />
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * One configured broker's launch registration URL(s): one line per launch
 * host (the path alone when no host is configured), each with a copy
 * button; the example of what the provider opens, as muted text; then
 * slug, Locked, environment and readiness. Plain text throughout — never a
 * link: opening it would hand {LAUNCH_CODE} to the gateway as a code.
 */
function LaunchCell({ item, bases, loading }: { item?: ProviderLaunchItem; bases: string[]; loading: boolean }) {
  if (loading) return <Skeleton className="h-10 w-48" />;
  if (!item) return <span className="text-muted">—</span>;
  const urls = (bases.length > 0 ? bases : [""]).map((b) => launchRegistrationURL(b, item.launch_slug));
  return (
    // min-w keeps break-all from collapsing the column to a few
    // characters; the table scrolls inside its card instead. Narrower on a
    // phone so the whole cell fits the card's scroll window at 375px.
    <div className="min-w-[15rem] max-w-[24rem] space-y-1.5 sm:min-w-[21rem]">
      {urls.map((u) => (
        <div key={u} className="flex items-start gap-1">
          <span className="min-w-0 break-all font-mono text-2xs text-t-1">{u}</span>
          <CopyButton value={u} label="launch URL" className="shrink-0" />
        </div>
      ))}
      <div className="text-2xs text-muted">
        <span>Your provider opens:</span> <span className="break-all font-mono">{exampleLaunchOpenURL(urls[0])}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-mono text-2xs">{item.launch_slug}</span>
        {item.launch_slug_locked ? <Badge tone="warning">Locked</Badge> : null}
        <span className="text-2xs text-t-2">{providerEnvironmentLabel(item.environment)}</span>
        {item.launch_ready ? (
          <Badge tone="success">Ready</Badge>
        ) : (
          <>
            <Badge tone="muted">Not ready</Badge>
            <span className="text-2xs text-muted">{launchNotReadyLabel(item.not_ready_reason)}</span>
          </>
        )}
      </div>
    </div>
  );
}
